/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Internal dependencies
 */
import calcRotatedResizeOffset from './calcRotatedResizeOffset';
import {
  getTextBoxAttachmentStack,
  isTextBoxAttachment,
  normalizeTextBoxAttachments,
} from './textBoxAttachment';

/**
 * Returns geometry updates for an automatic text height change while keeping
 * the configured local edge or centre fixed. Missing and invalid anchors
 * preserve the existing top-anchored behavior.
 *
 * @param {Object} element Text element geometry.
 * @param {number} newHeight Updated text height.
 * @return {{x: number, y: number, height: number}} Geometry updates.
 */
function getTextHeightResizeUpdates(element, newHeight) {
  const { x, y, height, rotationAngle = 0, verticalAnchor } = element;
  const heightDelta = newHeight - height;
  const topDelta =
    verticalAnchor === 'center'
      ? heightDelta / 2
      : verticalAnchor === 'bottom'
      ? heightDelta
      : 0;
  const [dx, dy] = calcRotatedResizeOffset(
    rotationAngle || 0,
    0,
    0,
    topDelta,
    heightDelta - topDelta
  );

  return {
    height: newHeight,
    x: x + dx,
    y: y + dy,
  };
}

/**
 * Returns text height updates while treating an attachment's own edge as the
 * authoritative resize anchor.
 *
 * @param {Object} element Text element geometry.
 * @param {number} newHeight Updated text height.
 * @return {{x: number, y: number, height: number}} Geometry updates.
 */
export function getTextElementHeightResizeUpdates(element, newHeight) {
  if (!isTextBoxAttachment(element.textBoxAttachment)) {
    return getTextHeightResizeUpdates(element, newHeight);
  }

  return getTextHeightResizeUpdates(
    { ...element, verticalAnchor: element.textBoxAttachment.ownEdge },
    newHeight
  );
}

// The native editor measures in saved page units, just like committed geometry.
export function getTextBoxAttachmentReflow(elements, element, newHeight) {
  const stack = getTextBoxAttachmentStack(elements, element.id);
  if (
    stack.length < 2 ||
    !['top', 'center', 'bottom'].includes(element.verticalAnchor)
  )
    return null;
  const previousElements = stack.map((member) =>
    member.id === element.id ? { ...member, ...element } : member
  );
  return normalizeTextBoxAttachments(
    previousElements.map((member) =>
      member.id === element.id
        ? {
            ...member,
            ...getTextElementHeightResizeUpdates(member, newHeight),
          }
        : member
    ),
    { previousElements }
  );
}

export default getTextHeightResizeUpdates;
